import { useState } from "react";
import { motion } from "framer-motion";
import { Palette, MoonStar, Music, Star, Crown, Check, Loader2, ShoppingBag, Sparkles } from "lucide-react";
import { createCheckout } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PRODUCTS = [
  {
    icon: Palette, color: "#FF6B6B", bg: "#FFE5E5",
    title: "Big Activity Bundle",
    desc: "40+ printable colouring pages, puzzles and sticker charts — every pack theme, one big download.",
    tag: "Printable PDF",
  },
  {
    icon: MoonStar, color: "#9333EA", bg: "#F3E8FF",
    title: "Bedtime Prayer Poster Pack",
    desc: "Gentle illustrated posters for the wall by little beds — the prayer from our 7-day pack and friends.",
    tag: "Printable PDF",
  },
  {
    icon: Music, color: "#0284C7", bg: "#E0F2FE",
    title: "Sing-Along Songbook",
    desc: "Every lyric from the library with simple chords — for kitchen discos and Sunday school alike.",
    tag: "Digital songbook",
  },
  {
    icon: Star, color: "#FFC107", bg: "#FEF3C7",
    title: "Kindness Reward Chart",
    desc: "A faith-filled reward chart with sticker sheets — praying, sharing, helping and saying sorry.",
    tag: "Printable PDF",
  },
];

const PERKS = [
  "Every product on this page, free",
  "The full song library + bedtime playlists",
  "All Bible stories with narration",
  "A kind, parent-first community",
];

const Shop = () => {
  const { user, openAuth } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const isPlus = user && user.plan === "family_plus";

  const startCheckout = async () => {
    if (!user) {
      openAuth("signup");
      return;
    }
    if (isPlus || checkingOut) return;
    setCheckingOut(true);
    try {
      const { checkout_url } = await createCheckout("family_plus_monthly");
      window.location.href = checkout_url;
    } catch {
      setCheckingOut(false);
    }
  };

  return (
    <section
      id="shop"
      className="relative min-h-[100svh] overflow-hidden bg-[#F8F5EC] py-16"
      data-testid="shop-section"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -right-24 top-16 h-80 w-80 rounded-full bg-[#FEF3C7] blur-3xl" />
        <div className="absolute -left-24 bottom-16 h-80 w-80 rounded-full bg-[#F3E8FF] blur-3xl" />
      </div>
      <div className="relative mx-auto w-full max-w-5xl px-4 sm:px-6">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-xs font-bold uppercase tracking-[0.3em] text-[#FFC107]">
            <ShoppingBag className="h-3.5 w-3.5" /> The Little Shop
          </span>
          <h2 className="mx-auto mt-6 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Treasures for your family&apos;s faith moments.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Printables and goodies from the Little Songs. Big Faith. universe — and every single
            one is <strong>free with Family+</strong>.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              whileHover={{ y: -6, rotate: i % 2 ? 1 : -1 }}
              className="flex flex-col rounded-3xl border border-[#E2DDD0] bg-white p-6 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.1)]"
              data-testid={`shop-product-${i}`}
            >
              <span className="grid h-12 w-12 place-items-center rounded-2xl" style={{ backgroundColor: p.bg }}>
                <p.icon className="h-6 w-6" style={{ color: p.color }} />
              </span>
              <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{p.title}</h3>
              <p className="mt-1.5 flex-1 text-sm leading-relaxed text-slate-500">{p.desc}</p>
              <div className="mt-4 flex items-center gap-2">
                <span className="inline-block rounded-full bg-[#DCFCE7] px-3 py-1 text-xs font-bold text-[#16A34A]">
                  Free with Family+
                </span>
                <span className="text-xs font-semibold text-slate-400">{p.tag}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Family+ membership card */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6 }}
          className="mt-10 overflow-hidden rounded-3xl bg-slate-900 p-8 text-center sm:p-10"
          data-testid="shop-family-plus-card"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#FFC107]">
            <Crown className="h-3.5 w-3.5" /> Family+ membership
          </span>
          <h3 className="mt-4 font-display text-2xl font-bold text-white sm:text-3xl">
            Everything above, plus the whole library.
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-300">
            £7.99 a month. Cancel anytime. Songs, stories, printables and community — one cosy home.
          </p>
          <ul className="mx-auto mt-6 grid max-w-md gap-2 text-left">
            {PERKS.map((perk) => (
              <li key={perk} className="flex items-center gap-3 text-sm font-semibold text-slate-200">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#16A34A]">
                  <Check className="h-3.5 w-3.5 text-white" />
                </span>
                {perk}
              </li>
            ))}
          </ul>
          {isPlus ? (
            <p className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#16A34A] px-8 py-4 font-display text-base font-semibold text-white" data-testid="shop-member-badge">
              <Sparkles className="h-4 w-4" /> You&apos;re in the family — welcome!
            </p>
          ) : (
            <button
              onClick={startCheckout}
              disabled={checkingOut}
              data-testid="shop-join-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FFC107] px-8 py-4 font-display text-base font-semibold text-slate-900 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.45)] transition-all hover:-translate-y-0.5 disabled:opacity-60"
            >
              {checkingOut && <Loader2 className="h-4 w-4 animate-spin" />}
              Join Family+ and get it all
            </button>
          )}
          <p className="mt-4 text-xs text-slate-400">Secure checkout &bull; cancel anytime &bull; instant access</p>
        </motion.div>

        <p className="mt-10 text-center font-hand text-2xl text-slate-400">
          every download helps a little heart grow
        </p>
      </div>
    </section>
  );
};

export default Shop;
